/**
 * Tenant-scoped chat service — mirrors mobile ChatService.
 * All reads/writes require activeTenantId; membership drives authorization.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase/firebase-config';
import {
  buildSystemChatRoomDocId,
  isSystemChatMessageUnit,
} from '../platform/chatRoomIds';
import { COLLECTIONS, ROLES } from '../platform/constants';
import { markChatRoomAsRead as markChatRoomAsReadShared } from '../platform/lastRead/lastReadService';
import {
  canCreateContentForTenant,
  effectiveRole,
  isTenantAdminForTenant,
} from '../platform/membershipPermissions';
import { buildUserSearchHaystack } from '../utils/userSearch';
import { withTenantScope } from './contentWriteFields';
import {
  filterByActiveTenant,
  shouldApplyServerTenantFilter,
} from './tenantContentFilter';
import { fetchUnitsForChat } from './unitsService';

export const CHAT_PAGE_SIZE = 20;

export function getChatMessageUnitId(room) {
  return room.messageUnit || room.id;
}

function messagesCollection() {
  return collection(db, COLLECTIONS.MESSAGES);
}

function chatRoomsCollection() {
  return collection(db, COLLECTIONS.CHAT_ROOMS);
}

function mapMessage(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    createdAt: data.createdAt ? data.createdAt.toMillis() : null,
  };
}

function filterMessages(messages, tenantId) {
  return filterByActiveTenant(messages, tenantId);
}

export function buildMessagesQuery({ tenantId, unitId, pageSize = CHAT_PAGE_SIZE, lastVisible = null }) {
  if (!shouldApplyServerTenantFilter(tenantId)) {
    throw new Error('[chat] activeTenantId is required');
  }

  let q = query(
    messagesCollection(),
    where('tenantId', '==', tenantId),
    where('unit', '==', unitId),
    orderBy('createdAt', 'desc'),
    limit(pageSize),
  );

  if (lastVisible) {
    q = query(
      messagesCollection(),
      where('tenantId', '==', tenantId),
      where('unit', '==', unitId),
      orderBy('createdAt', 'desc'),
      startAfter(lastVisible),
      limit(pageSize),
    );
  }

  return q;
}

export function canAccessChatRoom({ room, role, userUid, userUnit }) {
  const isAdmin = role === ROLES.ADMIN;
  const isLocalAdmin = role === ROLES.LOCAL_ADMIN;
  if (isAdmin || isLocalAdmin) return true;

  if (room.roomName || room.isChatRoom) {
    if (room.accessibleToAll) return true;
    if (room.accessibleToRoles?.includes(role)) return true;
    if (room.allowedUsers?.includes(userUid)) return true;
    if (room.unit && room.unit === userUnit) return true;
    return false;
  }

  return (
    room.accessibleToAll
    || room.accessibleToRoles?.includes(role)
    || room.id === userUnit
  );
}

function mapUnitRoom(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    lastMessageAt: data.lastMessageAt?.toDate?.() ?? null,
  };
}

function mapChatRoom(docSnap) {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    ...data,
    isChatRoom: true,
    lastMessageAt: data.lastMessageAt?.toDate?.() ?? null,
  };
}

function filterUnitsForChatRole(units, { role, userUnit }) {
  if (role === ROLES.ADMIN || role === ROLES.LOCAL_ADMIN) {
    return units;
  }
  if (!userUnit) {
    return [];
  }
  const match = units.find((unit) => unit.id === userUnit);
  return match ? [match] : [];
}

function mergeAccessibleRooms({
  units,
  chatRooms,
  tenantId,
  memberships,
  legacyUser,
}) {
  const role = effectiveRole(memberships, tenantId, legacyUser);
  const userUnit = legacyUser?.unit || '';
  const userUid = legacyUser?.uid || '';

  const merged = new Map();
  [...units, ...chatRooms].forEach((room) => {
    if (canAccessChatRoom({ room, role, userUid, userUnit })) {
      merged.set(room.id, room);
    }
  });

  // Legacy units that mirror a system chat room name (pre-platform duplicate).
  const systemRoomNames = new Set(
    chatRooms
      .filter((room) => room.system)
      .map((room) => String(room.roomName || room.unitName || '').trim().toLowerCase())
      .filter(Boolean),
  );

  return Array.from(merged.values())
    .filter((room) => {
      if (room.isChatRoom || room.roomName) return true;
      const unitName = String(room.unitName || '').trim().toLowerCase();
      return !systemRoomNames.has(unitName);
    }).sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : 999;
    const orderB = typeof b.order === 'number' ? b.order : 999;
    if (orderA !== orderB) return orderA - orderB;
    const nameA = a.roomName || a.unitName || a.id;
    const nameB = b.roomName || b.unitName || b.id;
    return nameA.localeCompare(nameB);
  });
}

export async function fetchAccessibleChatRooms({
  tenantId,
  memberships,
  legacyUser,
}) {
  const role = effectiveRole(memberships, tenantId, legacyUser);
  const userUnit = legacyUser?.unit || '';

  const rawUnits = await fetchUnitsForChat(tenantId, { role, userUnit });
  const units = rawUnits.map((unit) => ({
    ...unit,
    lastMessageAt: unit.lastMessageAt?.toDate?.() ?? unit.lastMessageAt ?? null,
  }));
  const chatRooms = await fetchChatRooms({ tenantId });

  return mergeAccessibleRooms({
    units,
    chatRooms,
    tenantId,
    memberships,
    legacyUser,
  });
}

export function subscribeAccessibleChatRooms({
  tenantId,
  memberships,
  legacyUser,
  onData,
  onError,
}) {
  if (!tenantId || !legacyUser) {
    return () => {};
  }

  const role = effectiveRole(memberships, tenantId, legacyUser);
  const userUnit = legacyUser?.unit || '';

  let units = [];
  let chatRooms = [];
  let unitsReady = false;
  let chatRoomsReady = false;

  const emit = () => {
    if (!unitsReady || !chatRoomsReady) return;
    onData(mergeAccessibleRooms({
      units,
      chatRooms,
      tenantId,
      memberships,
      legacyUser,
    }));
  };

  const unsubUnits = onSnapshot(
    query(
      collection(db, COLLECTIONS.UNITS),
      where('tenantId', '==', tenantId),
    ),
    (snapshot) => {
      const tenantUnits = filterByActiveTenant(snapshot.docs.map(mapUnitRoom), tenantId);
      units = filterUnitsForChatRole(tenantUnits, { role, userUnit });
      unitsReady = true;
      emit();
    },
    onError,
  );

  const unsubChatRooms = onSnapshot(
    query(chatRoomsCollection(), where('tenantId', '==', tenantId)),
    (snapshot) => {
      chatRooms = filterByActiveTenant(snapshot.docs.map(mapChatRoom), tenantId);
      chatRoomsReady = true;
      emit();
    },
    onError,
  );

  return () => {
    unsubUnits();
    unsubChatRooms();
  };
}

async function fetchChatRooms({ tenantId }) {
  const snapshot = await getDocs(
    query(chatRoomsCollection(), where('tenantId', '==', tenantId)),
  );
  return filterByActiveTenant(snapshot.docs.map(mapChatRoom), tenantId);
}

async function touchChatRoomLastMessageAt({ tenantId, unitId }) {
  if (!tenantId || !unitId) return;

  const timestamp = serverTimestamp();
  const updates = [];
  const touchedRoomIds = new Set();

  // 1. Tenant-scoped system room (e.g. chatRooms/triglav__general for messageUnit "general").
  const systemRoomRef = doc(
    db,
    COLLECTIONS.CHAT_ROOMS,
    buildSystemChatRoomDocId(tenantId, unitId),
  );
  const systemRoomSnap = await getDoc(systemRoomRef);
  if (systemRoomSnap.exists() && systemRoomSnap.data()?.tenantId === tenantId) {
    updates.push(updateDoc(systemRoomRef, { lastMessageAt: timestamp }));
    touchedRoomIds.add(systemRoomRef.id);
  }

  // 2. Custom chat room whose document id equals the message unit.
  if (!touchedRoomIds.has(unitId)) {
    const directRoomRef = doc(db, COLLECTIONS.CHAT_ROOMS, unitId);
    const directRoomSnap = await getDoc(directRoomRef);
    if (directRoomSnap.exists() && directRoomSnap.data()?.tenantId === tenantId) {
      updates.push(updateDoc(directRoomRef, { lastMessageAt: timestamp }));
      touchedRoomIds.add(unitId);
    }
  }

  // 3. Organizational unit chat — only real units/{id} docs for this tenant.
  // Skip semantic system channels (general/conference); they must never map to units/general.
  if (!isSystemChatMessageUnit(unitId) && !touchedRoomIds.has(unitId)) {
    try {
      const unitRef = doc(db, COLLECTIONS.UNITS, unitId);
      const unitSnap = await getDoc(unitRef);
      if (unitSnap.exists() && unitSnap.data()?.tenantId === tenantId) {
        updates.push(updateDoc(unitRef, { lastMessageAt: timestamp }));
      }
    } catch (error) {
      console.warn('[chat] skip units lastMessageAt touch', { tenantId, unitId, error });
    }
  }

  if (updates.length > 0) {
    await Promise.all(updates);
  }
}

export function subscribeToMessages({ tenantId, unitId, onData, onError }) {
  const q = buildMessagesQuery({ tenantId, unitId });
  return onSnapshot(
    q,
    (snapshot) => {
      const messages = filterMessages(
        snapshot.docs.map(mapMessage).reverse(),
        tenantId,
      );
      onData({
        messages,
        lastVisible: snapshot.docs[snapshot.docs.length - 1] ?? null,
      });
    },
    onError,
  );
}

export async function loadOlderMessages({ tenantId, unitId, lastVisible }) {
  const snapshot = await getDocs(
    buildMessagesQuery({ tenantId, unitId, lastVisible }),
  );
  return {
    messages: filterMessages(snapshot.docs.map(mapMessage).reverse(), tenantId),
    lastVisible: snapshot.docs[snapshot.docs.length - 1] ?? null,
  };
}

export async function sendChatMessage({
  tenantId,
  unitId,
  text,
  user,
}) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return;

  await addDoc(
    messagesCollection(),
    withTenantScope(tenantId, {
      text: trimmed,
      createdAt: serverTimestamp(),
      uid: user.uid,
      displayName: user.displayName,
      email: user.email,
      unit: unitId,
    }),
  );

  try {
    await touchChatRoomLastMessageAt({ tenantId, unitId });
  } catch (error) {
    console.warn('[chat] lastMessageAt touch failed; message was sent', {
      tenantId,
      unitId,
      error,
    });
  }
}

export async function deleteChatMessage(messageId) {
  await deleteDoc(doc(messagesCollection(), messageId));
}

export async function markChatRoomAsRead(userId, unitId) {
  await markChatRoomAsReadShared(userId, unitId);
}

export function isChatAdmin({ memberships, tenantId, legacyUser }) {
  return isTenantAdminForTenant(memberships, tenantId, legacyUser);
}

export function canManageChatRooms({ memberships, tenantId, legacyUser }) {
  return canCreateContentForTenant(memberships, tenantId, legacyUser);
}

function mapUserForPicker(userDoc, membership = {}) {
  const data = userDoc.data();
  const firstName = String(data.firstName || '').trim();
  const lastName = String(data.lastName || '').trim();
  const fullName = `${firstName} ${lastName}`.trim();
  const displayName =
    String(data.displayName || '').trim() || fullName || data.email || userDoc.id;
  const email = String(data.email || '').trim();
  const jobPosition = String(membership.jobPosition || data.jobPosition || '').trim();
  const workArea = String(membership.workArea || data.workArea || '').trim();
  const user = {
    id: userDoc.id,
    displayName,
    email,
    firstName,
    lastName,
    phone: String(data.phone || '').trim(),
    jobPosition,
    workArea,
  };
  const searchHaystack = buildUserSearchHaystack(user);

  return {
    ...user,
    searchHaystack,
    searchText: searchHaystack,
  };
}

/**
 * Users for admin pickers (chat room access, etc.).
 * Loads active tenant members, then fetches user profiles in parallel.
 */
export async function fetchTenantMemberUsers(tenantId) {
  if (!tenantId) {
    return [];
  }

  const membersSnap = await getDocs(
    collection(db, COLLECTIONS.TENANTS, tenantId, COLLECTIONS.MEMBERS),
  );

  const memberById = new Map(
    membersSnap.docs.map((memberDoc) => [memberDoc.id, memberDoc.data() || {}]),
  );

  const activeMemberIds = membersSnap.docs
    .filter((memberDoc) => memberDoc.data()?.status !== 'inactive')
    .map((memberDoc) => memberDoc.id);

  if (activeMemberIds.length === 0) {
    return [];
  }

  const userSnaps = await Promise.all(
    activeMemberIds.map((uid) => getDoc(doc(db, COLLECTIONS.USERS, uid))),
  );

  return userSnaps
    .filter((userSnap) => userSnap.exists())
    .map((userSnap) => mapUserForPicker(userSnap, memberById.get(userSnap.id) || {}))
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'sl'));
}
